import type { ExecutionRunCounts } from './ExecutionRunsTableV2RowHelpers';
import type { SuiteRunSummaryDto, TestRunSummaryDto } from '@flowqa/shared';

export type SortableNestedSuiteRunField =
  | 'durationMs'
  | 'runNumber'
  | 'status'
  | 'tests'
  | 'timestamp';
export type SortableNestedTestRunField = 'durationMs' | 'runNumber' | 'status' | 'timestamp';

export const defaultNestedSuiteRunSort = { field: 'timestamp', direction: 'desc' } as const;
export const defaultNestedTestRunSort = { field: 'timestamp', direction: 'desc' } as const;

export const countTestSummaries = (tests: TestRunSummaryDto[]): ExecutionRunCounts =>
  tests.reduce<ExecutionRunCounts>(
    (counts, test) => {
      const status = test.status ?? 'untested';
      const nextCounts = { ...counts, total: counts.total + 1 };
      nextCounts[status] += 1;
      return nextCounts;
    },
    { total: 0, passed: 0, failed: 0, errored: 0, skipped: 0, untested: 0 },
  );

const normaliseSummaryText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const fallbackSummaryName = 'Untitled';

export const getSuiteSummaryName = (summary: SuiteRunSummaryDto): string => {
  const displayName = normaliseSummaryText(summary.displayName);
  if (displayName) return displayName;
  const suiteNameValue = normaliseSummaryText(summary.suiteName);
  return suiteNameValue.length > 0 ? suiteNameValue : fallbackSummaryName;
};

export const getTestSummaryName = (summary: TestRunSummaryDto): string => {
  const displayName = normaliseSummaryText(summary.displayName);
  if (displayName) return displayName;
  const testNameValue = normaliseSummaryText(summary.testName);
  return testNameValue.length > 0 ? testNameValue : fallbackSummaryName;
};

export const getSummaryName = (summary: SuiteRunSummaryDto | TestRunSummaryDto): string => {
  if ('testId' in summary) return getTestSummaryName(summary);
  if ('suiteId' in summary) return getSuiteSummaryName(summary);
  return fallbackSummaryName;
};

export const getSuiteKeyForTest = (summary: TestRunSummaryDto): string =>
  summary.suiteDisplayName ?? summary.suiteName ?? 'Other tests';

const compareStrings = (left: string, right: string, direction: 'asc' | 'desc'): number =>
  direction === 'asc' ? left.localeCompare(right) : right.localeCompare(left);

const compareNullableNumbers = (
  left: number | null | undefined,
  right: number | null | undefined,
  direction: 'asc' | 'desc',
): number => {
  const leftValue = left ?? null;
  const rightValue = right ?? null;
  if (leftValue === null && rightValue === null) return 0;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;
  return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
};

const resolveDateTime = (value: Date | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.valueOf()) ? null : date.getTime();
};

const compareNullableDates = (
  left: Date | string | null | undefined,
  right: Date | string | null | undefined,
  direction: 'asc' | 'desc',
): number => compareNullableNumbers(resolveDateTime(left), resolveDateTime(right), direction);

const resolveSuitePassRate = (
  suite: SuiteRunSummaryDto,
  testsBySuite: Map<string, TestRunSummaryDto[]>,
): number | null => {
  const counts = countTestSummaries(testsBySuite.get(getSummaryName(suite)) ?? []);
  return counts.total > 0 ? counts.passed / counts.total : null;
};

const compareSuiteRunSummaries = (
  left: SuiteRunSummaryDto,
  right: SuiteRunSummaryDto,
  field: SortableNestedSuiteRunField,
  direction: 'asc' | 'desc',
  testsBySuite: Map<string, TestRunSummaryDto[]>,
): number => {
  if (field === 'durationMs') {
    return compareNullableNumbers(left.durationMs, right.durationMs, direction);
  }
  if (field === 'runNumber') {
    return compareNullableNumbers(left.runNumber, right.runNumber, direction);
  }
  if (field === 'status') {
    return compareStrings(left.status ?? '', right.status ?? '', direction);
  }
  if (field === 'tests') {
    return compareNullableNumbers(
      resolveSuitePassRate(left, testsBySuite),
      resolveSuitePassRate(right, testsBySuite),
      direction,
    );
  }
  return compareNullableDates(left.timestamp, right.timestamp, direction);
};

const compareTestRunSummaries = (
  left: TestRunSummaryDto,
  right: TestRunSummaryDto,
  field: SortableNestedTestRunField,
  direction: 'asc' | 'desc',
): number => {
  if (field === 'durationMs') {
    return compareNullableNumbers(left.durationMs, right.durationMs, direction);
  }
  if (field === 'runNumber') {
    return compareNullableNumbers(left.runNumber, right.runNumber, direction);
  }
  if (field === 'status') {
    return compareStrings(left.status ?? '', right.status ?? '', direction);
  }
  return compareNullableDates(left.timestamp, right.timestamp, direction);
};

export const sortSuiteRunSummaries = (
  suites: SuiteRunSummaryDto[],
  sort: { field: SortableNestedSuiteRunField; direction: 'asc' | 'desc' },
  testsBySuite: Map<string, TestRunSummaryDto[]>,
): SuiteRunSummaryDto[] =>
  [...suites].sort((left, right) => {
    const primary = compareSuiteRunSummaries(left, right, sort.field, sort.direction, testsBySuite);
    if (primary !== 0) return primary;
    return compareStrings(getSummaryName(left), getSummaryName(right), 'asc');
  });

export const sortTestRunSummaries = (
  tests: TestRunSummaryDto[],
  sort: { field: SortableNestedTestRunField; direction: 'asc' | 'desc' },
): TestRunSummaryDto[] =>
  [...tests].sort((left, right) => {
    const primary = compareTestRunSummaries(left, right, sort.field, sort.direction);
    if (primary !== 0) return primary;
    return compareStrings(getSummaryName(left), getSummaryName(right), 'asc');
  });
